import type { LandingLocale } from "../landingCopy";

/**
 * Så fungerar Caire: Traditionellt vs Caire (direction 3b) and the cleaning
 * exception (3c). The same breakfast and cleaning visits as the four layers on
 * the homepage. Example scenario; times are illustrative.
 */
export interface HowItWorksCopy {
  compare: {
    eyebrow: string;
    title: string;
    lead: string;
    traditional: { title: string; tag: string; text: string; rows: string[] };
    caire: { title: string; tag: string; text: string; rows: string[] };
    lateNote: string;
    rowValues: { traditional: string[]; caire: string[] };
  };
  exception: {
    eyebrow: string;
    title: string;
    lead: string;
    simulate: string;
    reset: string;
    facts: string[];
    weekdays: string[];
    weekLabel: (week: number) => string;
    breakfast: string;
    cleaning: string;
    limited: string;
    previous: string;
    normal: string;
    shortage: string;
    moved: string;
    example: string;
  };
}

const sv: HowItWorksCopy = {
  compare: {
    eyebrow: "Varför Caire",
    title: "Tre planer som borde vara en.",
    lead: "Frukost måndag, 30 minuter. Så här ser samma besök ut genom dagen.",
    traditional: {
      title: "Traditionellt",
      tag: "Tre olika svar",
      text: "Besöksplaneringen skiljer sig redan från genomförandeplanen. Ett sms på morgonen om man har tur. Personalen kommer sent och ingen visste i förväg.",
      rows: [
        "Genomförandeplan",
        "Besöksplanering",
        "Sms på morgonen",
        "Utfört",
      ],
    },
    caire: {
      title: "Med Caire",
      tag: "Samma plan",
      text: "Genomförandeplan, besöksplanering och utfört stämmer, eftersom restid och bemanning är inräknade från början. Flexen på ±15 minuter syns för alla.",
      rows: ["Genomförandeplan", "Planerat besök", "I appen", "Utfört"],
    },
    lateNote: "45 min sent",
    rowValues: {
      traditional: ["08:00", "08:20", "”runt 8”", "08:45"],
      caire: ["08:00 ±15", "08:09 · Anna", "veckan innan", "08:09"],
    },
  },
  exception: {
    eyebrow: "När det är ont om folk",
    title: "Städet har en fast dag. Det flyttas bara i undantagsfall.",
    lead: "Tidsbundna behov som frukost och toalett går alltid först. En serviceinsats som städ styrs av hur ofta den ska ske. Räcker inte personalen kan den undantagsvis flyttas inom beslutets två veckor, och sedan tillbaka till sin vanliga dag.",
    simulate: "Simulera personalbrist tisdag 6 okt",
    reset: "Återställ",
    facts: [
      "Beviljat · 90 min varannan vecka",
      "Genomförandeplan · tisdagar varannan v. · 14:30 ±45",
      "Undantag · inom v. 41–42",
      "Prioritet · frukost och toalett 1 · städ 2",
    ],
    weekdays: ["Mån", "Tis", "Ons", "Tor", "Fre"],
    weekLabel: (week) => `v. ${week}`,
    breakfast: "08:00 Frukost",
    cleaning: "Städ · 90 min",
    limited: "Begränsat",
    previous: "tidigare plats",
    normal:
      "Normalfallet: städet ligger på sin fasta dag enligt genomförandeplanen, tisdag 6 okt 14:10–15:40, precis som vilket besök som helst.",
    shortage:
      "Undantag: tisdag 6 okt räcker inte personalen. Frukost och toalett ligger kvar. Städet flyttas den här gången till onsdag 14 okt, inom beslutets två veckor. Nästa gång är det tillbaka på tisdag, och brukaren ser ändringen direkt i appen.",
    moved: "14:10–15:40 · flyttad",
    example: "Exempelscenario",
  },
};

const en: HowItWorksCopy = {
  compare: {
    eyebrow: "Why Caire",
    title: "Three plans that should be one.",
    lead: "Breakfast on Monday, 30 minutes. This is the same visit through the day.",
    traditional: {
      title: "Traditionally",
      tag: "Three different answers",
      text: "Visit planning already differs from the care plan. A text message in the morning if you are lucky. The caregiver arrives late and nobody knew in advance.",
      rows: ["Care plan", "Visit planning", "Morning text", "Performed"],
    },
    caire: {
      title: "With Caire",
      tag: "One plan",
      text: "Care plan, visit planning and performed agree, because travel and staffing are included from the start. The ±15 minute flex is visible to everyone.",
      rows: ["Care plan", "Planned visit", "In the app", "Performed"],
    },
    lateNote: "45 min late",
    rowValues: {
      traditional: ["08:00", "08:20", "“around 8”", "08:45"],
      caire: ["08:00 ±15", "08:09 · Anna", "the week before", "08:09"],
    },
  },
  exception: {
    eyebrow: "When staff are short",
    title: "Cleaning has a fixed day. It moves only as an exception.",
    lead: "Time-bound needs such as breakfast and toileting always come first. A service visit such as cleaning is set by how often it happens. If staff are short it may move within the decision's two weeks as an exception, then back to its usual day.",
    simulate: "Simulate a staff shortage on Tuesday 6 Oct",
    reset: "Reset",
    facts: [
      "Granted · 90 min every other week",
      "Care plan · Tuesdays, alternate weeks · 14:30 ±45",
      "Exception · within weeks 41–42",
      "Priority · breakfast and toileting 1 · cleaning 2",
    ],
    weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    weekLabel: (week) => `Week ${week}`,
    breakfast: "08:00 Breakfast",
    cleaning: "Cleaning · 90 min",
    limited: "Limited",
    previous: "previous slot",
    normal:
      "The normal case: cleaning is on its fixed day from the care plan, Tuesday 6 Oct 14:10–15:40, like any other visit.",
    shortage:
      "Exception: staff are short on Tuesday 6 Oct. Breakfast and toileting stay. This time cleaning moves to Wednesday 14 Oct, within the decision's two weeks. Next time it is back on Tuesday, and the client sees the change in the app.",
    moved: "14:10–15:40 · moved",
    example: "Example scenario",
  },
};

export const howItWorksCopy: Record<LandingLocale, HowItWorksCopy> = { sv, en };
